import { useMemo, useRef, useState, useEffect, ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  RfUiAccordionCustom,
  RfUiButton,
  RfUiInput,
  RfUiModal,
  RfUiMultiselect,
  RfUiSelect,
} from 'renfe-components';
import { createStructuredSelector } from 'reselect';
import {
  makeSelectCursosInformesParsed,
  makeSelectEstadosInformesParsed,
  makeSelectProvinciasInformesParsed,
  makeSelectSociedadesInformesParsed,
} from 'store/informes/selectors';
import {
  makeSelectFiltros,
  makeSelectNombreFiltro,
  makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected,
  makeSelectPerfilesUsuarioIdUsuario,
  makeSelectPerfilesUsuarioNombreUsuario,
  makesSelectFiltrosParse,
} from 'store/usuarios/selectors';
import { getActualTime, getMonthName } from 'utils/dates-validators';
import { language } from 'utils/literals';
import 'scss/03_generic/literals.scss';
import { isMobileOnly } from 'react-device-detect';
import { makeSelectInstructoresPracticasParse2 } from 'store/practicas/selectors';
import { arrayToAPI, monthArrayToAPI } from 'utils/listUtils';
import { RouteTypes } from 'consts';
import {
  getInstructoresPracticasRequest,
  removePracticasFilter,
} from 'store/practicas/actions';
import { ErrorMessage } from 'components/ErrorMessage/ErrorMessage';
import { useInformMesesError } from 'hooks/informFilterDatesError';
import { deleteSavedPagination } from 'utils/practiceUtils';
import { FilterContainer } from 'containers/filterContainer';
import { links } from '..';
import { editarFiltroRequest, eliminarFiltroRequest, getFiltrosRequest, guardarFiltroRequest, removeFiltro, setFiltroFechaCreacion, setFiltroFechaModificacion, setFiltroId, setFiltroJson, setFiltroNombre, setFiltroNombrePagina } from 'store/usuarios/actions';
import { useHistory } from 'react-router';

const selectors = createStructuredSelector({
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  sociedades: makeSelectSociedadesInformesParsed(),
  idUsuario: makeSelectPerfilesUsuarioIdUsuario(),
  nombreUsuario: makeSelectPerfilesUsuarioNombreUsuario(),
  provincias: makeSelectProvinciasInformesParsed(),
  estados: makeSelectEstadosInformesParsed(),
  cursos: makeSelectCursosInformesParsed(),
  instructores: makeSelectInstructoresPracticasParse2(),
  filtro: makeSelectFiltros(),
  filtros: makesSelectFiltrosParse(),
  nombreFiltro: makeSelectNombreFiltro(),
  instructoresFiltro: makeSelectInstructoresPracticasParse2(),
} as any);


type FilterOption = { label: string; value: any; active?: string };

const parseFilterOptions = (raw: any): FilterOption[] => {
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const parseFilterOptionsSilent = (raw: any): FilterOption[] => {
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (error) {
    return [];
  }
};

const getFiltroValueMonthIns = (meses: any): string[] => {
  if (Array.isArray(meses)) {
    return meses.map((mes: number | string) => mes.toString());
  }

  if (typeof meses === 'string') {
    return meses.split(',').map((mes) => mes.trim());
  }

  return [meses.toString()];
};

const buildUpdatedInstructores = (
  instructoresFiltro: any,
  filtroValueInstructores: string
): FilterOption[] => {
  const parsedInstructores = parseFilterOptionsSilent(instructoresFiltro);
  let updatedInstructores: FilterOption[] = [];

  if (Array.isArray(parsedInstructores)) {
    const uniqueInstructores = new Set<string>();
    updatedInstructores = parsedInstructores
      .filter((instructor) => {
        const instructorValue = instructor.value.toString();
        const filtroInstructorValues = filtroValueInstructores.toString().split(',');
        return (
          filtroInstructorValues.includes(instructorValue) ||
          filtroInstructorValues.includes(instructorValue.replace(/^L/, '')) ||
          filtroInstructorValues.includes('L' + instructorValue)
        );
      })
      .filter((instructor) => {
        const instructorValue = instructor.value.toString();
        if (uniqueInstructores.has(instructorValue)) {
          return false;
        } else {
          uniqueInstructores.add(instructorValue);
          return true;
        }
      })
      .map((instructor) => ({
        ...instructor,
        active: 'true',
      }));
  }

  filtroValueInstructores.split(',').forEach((instructorId: string) => {
    if (!updatedInstructores.some((instructor) => instructor.value === instructorId)) {
      const instructorPractica = parsedInstructores.find((instructor) => instructor.value === instructorId);
      if (instructorPractica) {
        updatedInstructores.push({
          label: `${instructorId} - ${instructorPractica.label.split(' - ')[1]}`,
          value: instructorId,
          active: 'false',
        });
      }
    }
  });

  return updatedInstructores;
};

const buildUpdatedAnyos = (
  calculatedAnyoSelectedOptions: any,
  filtroValueAnyo: number
): FilterOption[] =>
  parseFilterOptions(calculatedAnyoSelectedOptions).map((anyo) => {
    if (anyo.value === filtroValueAnyo) {
      return { ...anyo, active: 'true' };
    }
    return { ...anyo, active: 'false' };
  });

const buildUpdatedMeses = (
  calculatedMonthSelectedOptions: any,
  filtroValueMonth: any
): FilterOption[] =>
  parseFilterOptions(calculatedMonthSelectedOptions).map((mes) => {
    const mesValue = Number(mes.value);
    const mesLabel = getMonthName(mesValue);
    const mesValueInternal = (mesValue - 1).toString();
    const isActive = filtroValueMonth.toString() === mesValueInternal;

    return {
      ...mes,
      label: mesLabel,
      value: mesValue,
      active: isActive ? 'true' : 'false',
    };
  });

const buildUpdatedMesesIns = (
  calculatedMonthInsSelectedOptions: any,
  filtroValueMonthIns: string[]
): FilterOption[] => {
  const parsedMesesIns = parseFilterOptions(calculatedMonthInsSelectedOptions);

  filtroValueMonthIns.forEach((mes) => {
    const exists = parsedMesesIns.some((m) => m.value === mes);
    if (!exists) {
      parsedMesesIns.push({
        label: '',
        value: mes,
        active: 'false',
      });
    }
  });

  return parsedMesesIns.map((mes) => {
    const originalValue = mes.value.toString();
    const mesValue = Number(originalValue) + 1;
    const mesLabel = getMonthName(mesValue);
    const isActive = filtroValueMonthIns.includes(originalValue);

    return {
      ...mes,
      label: mesLabel,
      value: mesValue.toString(),
      active: isActive ? 'true' : 'false',
    };
  });
};


const getInitialSelectedMonth = (iniFilter: any, profileActive: any) => {
  if (iniFilter.savedValues && profileActive?.ID_PERFIL !== '211') {
    return {
      label: getMonthName(Number(iniFilter.meses) + 1),
      value: Number(iniFilter.meses) + 1,
      active: true,
    };
  }

  return {
    label: getMonthName(new Date().getMonth() + 1),
    value: new Date().getMonth() + 1,
    active: true,
  };
};

const getDefaultMonthsIns = (profileActive: any, iniFilter: any) => {
  if (profileActive?.ID_PERFIL !== '211') {
    return [
      {
        label: getMonthName(new Date().getMonth() + 1),
        value: (new Date().getMonth() + 1).toString(),
      },
    ];
  }

  if (iniFilter?.meses?.toString().includes(',')) {
    return iniFilter.meses.split(',').map((mes: number) => ({
      label: getMonthName(Number(mes) + 1),
      value: (Number(mes) + 1).toString(),
    }));
  }

  if (iniFilter?.meses !== undefined) {
    return [
      {
        label: getMonthName(iniFilter.meses + 1),
        value: iniFilter.meses.toString(),
      },
    ];
  }

  return [];
};

const normalizeOptions = (options: any[]) =>
  options.map((opt) => ({
    ...opt,
    value: opt.value.toString(),
  }));

const getUniqueOptions = (options: any[]) =>
  options.filter((value, index, self) => {
    return index === self.findIndex((t) => t.value.toString() === value.value.toString());
  });

export const ReportInstructorHoursFilter = ({ handleSubmit }): ReactElement => {
  const {
    profileActive,
    instructores,
    idUsuario,
    nombreUsuario,
    filtro,
    filtros,
    nombreFiltro,
    instructoresFiltro,
  } = useSelector<any, any>(selectors);

  const dispatch = useDispatch();
  const history = useHistory();

  const filterRef: any = useRef({});
  const [selectedInstructores, setSelectedInstructores]: [any, any] = useState('');
  const [instructorResetKey, setInstructorResetKey] = useState(0);
  const resetInstructorPending = useRef(true);

  const hasMultiselectOptions = (options: any): boolean => {
    if (Array.isArray(options)) {
      return options.length > 0;
    }

    if (typeof options === 'string') {
      try {
        const parsedOptions = JSON.parse(options);

        return Array.isArray(parsedOptions) && parsedOptions.length > 0;
      } catch {
        return false;
      }
    }

    return false;
  };

  useEffect(() => {
    if (
      resetInstructorPending.current &&
      hasMultiselectOptions(instructores)
    ) {
      setInstructorResetKey(k => k + 1);
      resetInstructorPending.current = false;
    }
  }, [instructores]);

  const initFilter = {
    meses: new Date().getMonth(),
    anyo: new Date().getFullYear(),
    instructores: profileActive?.ID_PERFIL === '211' ? idUsuario : '',
    elemInicio: 0,
    elemFin: 25,
    elemFinal: 25, // Algunas peticiones cambian el nombre del parámetro
    savedValues: false,
    lastProfile: profileActive?.ID_PERFIL,
  };

  const [iniFilter, setIniFilter] = useState(
    window.location.pathname === RouteTypes.REPORT_HOURS_INSTRUCTOR
      ? JSON.parse(
        sessionStorage.getItem(`savedFilter-${window.location.pathname}`) ||
        JSON.stringify(initFilter)
      )
      : initFilter
  );

  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  const [selectedMonth, setSelectedMonth] = useState(
    getInitialSelectedMonth(iniFilter, profileActive)
  );

  const [selectedMonthIns, setSelectedMonthIns] = useState(
    JSON.stringify(getDefaultMonthsIns(profileActive, iniFilter))
  );

  const generateArrayOfYears = () => {
    const max = new Date().getFullYear();
    const min = 2022;
    const years: Array<number> = [];

    for (let i: number = max; i >= min; i--) {
      years.push(i);
    }
    return years;
  };

  const anyos = generateArrayOfYears();

  const parseAnyos = (anyo): any => {
    const result = {
      label: '',
      value: 0,
      active: false,
    };
    result.label = anyo.toString();
    result.value = Number(anyo);
    result.active = true;

    return result;
  };

  const [selectedAnyo, setSelectedAnyo] = useState(
    iniFilter.savedValues
      ? {
        label: iniFilter.anyo,
        value: iniFilter.anyo,
        active: true,
      }
      : {
        label: new Date().getFullYear(),
        value: new Date().getFullYear(),
        active: true,
      }
  );

  useEffect(() => {
    let instructorArray = new Array();
    if (iniFilter.instructores !== '') {
      iniFilter.instructores.split(',').forEach((filterInstructors) => {
        const newInstructors = JSON.parse(instructores).filter((i) => {
          return i.value.toString() === filterInstructors;
        });
        instructorArray.push({
          label: newInstructors[0].label.toString(),
          value: newInstructors[0].value.toString(),
        });
      });
    }
    setSelectedInstructores(JSON.stringify(instructorArray));
  }, []);


  const handleChangeAnyoSelected = (e: CustomEvent) => {
    if (e.detail?.value) {
      const detailData = e.detail.value;
      const selectedYear = parseAnyos(detailData);

      setSelectedAnyo(selectedYear);

      const newFilter = {
        ...filterRef.current,
        anyo: selectedYear.value,
      };

      filterRef.current = newFilter;
      setIniFilter(newFilter);
    }
  };

  const calculatedAnyoSelectedOptions = useMemo(() => {
    const options = anyos.map((e: number) => ({
      label: e.toString(),
      value: e,
      active: selectedAnyo?.value === e ? 'true' : 'false',
    }));
    return JSON.stringify(options);
  }, [anyos, selectedAnyo]);

  const calculatedAnyoSelectedLabel: string = useMemo(() => {
    const anyoSel = selectedAnyo?.value ?? '';
    if (anyoSel && anyos) {
      const anyo = anyos.find((anyo) => anyo === anyoSel);
      if (anyo) {
        return anyo.toString();
      }
    }
    return '';
  }, [anyos, selectedAnyo]);

  /*
   *
   * End anyos
   *
   */

  /*
   *
   * Start months
   *
   */

  const parseMonths = (month): any => {
    const result = {
      label: '',
      value: 0,
      active: false,
    };
    result.label = getMonthName(month);
    result.value = month;
    result.active = true;

    return result;
  };


  const handleChangeMonthSelected = (e: CustomEvent) => {
    if (e.detail?.value) {
      const detailData = e.detail.value;
      const parsed = parseMonths(detailData);

      setSelectedMonth(parsed);

      const newFilter = {
        ...iniFilter,
        meses: parsed.value - 1,
      };

      filterRef.current = { ...newFilter };
      setIniFilter(newFilter);
    }
  };

  const calculatedMonthSelectedOptions = useMemo(() => {
    const options = months.map((e: number) => ({
      label: getMonthName(e),
      value: e,
      active: selectedMonth?.value === e ? 'true' : 'false',
    }));
    return JSON.stringify(options);
  }, [months, selectedMonth]);

  const calculatedMonthInsSelectedOptions = useMemo(() => {
    const options = months.map((e: number) => ({
      label: getMonthName(e),
      value: e.toString(),
    }));
    return JSON.stringify(options);
  }, [months]);

  const calculatedMonthSelectedLabel: string = useMemo(() => {
    const monthSel = selectedMonth?.value ?? '';
    if (monthSel && months) {
      const month = months.find((month) => month === monthSel);
      if (month) {
        return month.toString();
      }
    }
    return '';
  }, [months, selectedMonth]);


  const handleMonthInsSelected = (e: any) => {
    const meses = monthArrayToAPI(e.detail);

    const newFilter = {
      ...filterRef.current,
      meses: meses,
    };

    filterRef.current = newFilter;
    setIniFilter(newFilter);
  };

  const errorMeses = useInformMesesError(iniFilter.meses);

  /*
   *
   * End months
   *
   */

  const handleSelectedInstructor = (e: any) => {
    const selected = e.detail ?? [];

    const opcionesInstructores =
      typeof instructores === 'string'
        ? JSON.parse(instructores)
        : instructores;

    const allSelected =
      Array.isArray(opcionesInstructores) &&
      opcionesInstructores.length > 0 &&
      selected.length === opcionesInstructores.length;

    const instructoresSeleccionados = allSelected
      ? ''
      : arrayToAPI(selected);

    const newFilter = {
      ...iniFilter,
      instructores: instructoresSeleccionados,
    };

    setIniFilter(newFilter);
  };

  useEffect(() => {
    filterRef.current = { ...iniFilter };
  }, [iniFilter]);

  const handlerSubmitFilters = () => {
    deleteSavedPagination(window.location.pathname);
    sessionStorage.setItem(
      `savedFilter-${window.location.pathname}`,
      JSON.stringify(filterRef.current)
    );
    handleSubmit(filterRef.current);
  };

  const cleanFilters = () => {
    deleteSavedPagination(window.location.pathname);
    sessionStorage.setItem(
      `savedFilter-${window.location.pathname}`,
      JSON.stringify(initFilter)
    );
    dispatch(removePracticasFilter());

    // Limpiar filtro guardado activo
    setSelectedOptionFilter(false);
    setIsFilterActive(false);
    setOptionsFilter('');
    setAnyoFilter('');
    setMesesFilter('');
    setSelectedFilterName('');
    setSelectedFilterId('');

    setIniFilter(initFilter);
    handleSubmit(initFilter);
    setSelectedInstructores('[]');
    setSelectedAnyo({
      label: new Date().getFullYear(),
      value: new Date().getFullYear(),
      active: true,
    });
    setSelectedMonth({
      label: getMonthName(new Date().getMonth() + 1),
      value: new Date().getMonth() + 1,
      active: true,
    });
    setSelectedMonthIns(
      JSON.stringify([
        {
          label: getMonthName(new Date().getMonth() + 1),
          value: new Date().getMonth() + 1,
        },
      ])
    );

    // Al volver a cargar instructores remontar el multiselect
    resetInstructorPending.current = true;

    dispatch(getInstructoresPracticasRequest());
    handleSubmit(initFilter);
  };

  useEffect(() => {
    if (profileActive && iniFilter.lastProfile !== profileActive?.ID_PERFIL) {
      cleanFilters();
    }
  }, [profileActive]);

  //Filtro
  const [selectedFilterName, setSelectedFilterName] = useState('');
  const [modalCerradoFiltroGuardarType, setModalCerradoFiltroGuardarType] = useState('');
  const [modalCerradoFiltroEliminarType, setModalCerradoFiltroEliminarType] = useState('');
  const [modalCerradoFiltroEditarType, setModalCerradoFiltroEditarType] = useState('');
  const [selectedFilterId, setSelectedFilterId] = useState('');
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [optionsFilter, setOptionsFilter] = useState('');
  const [anyoFilter, setAnyoFilter] = useState('');
  const [mesesFilter, setMesesFilter] = useState('');
  const [selectedOptionFilter, setSelectedOptionFilter] = useState(false);

  const filterNameAdd: boolean = useMemo(() => {
    return !!nombreFiltro;
  }, [nombreFiltro]);

  const handleSelectedFiltros = (e: any) => {
    setSelectedOptionFilter(true);
    setIsFilterActive(true);
    const filtroSeleccionado = e.detail;
    setSelectedFilterName(filtroSeleccionado.label);
    const filtroId = filtroSeleccionado.label.split(' - ')[0];
    setSelectedFilterId(filtroId);

    const filtroValue = JSON.parse(filtroSeleccionado.value);
    const updatedInstructores = buildUpdatedInstructores(
      instructoresFiltro,
      filtroValue.instructores
    );
    const updatedAnyos = buildUpdatedAnyos(
      calculatedAnyoSelectedOptions,
      filtroValue.anyo
    );
    const filteredAnyos = updatedAnyos.filter((anyo) => anyo.active === 'true');
    const updatedMeses = buildUpdatedMeses(
      calculatedMonthSelectedOptions,
      filtroValue.meses
    );
    const filteredMeses = updatedMeses.filter((mes) => mes.active === 'true');
    const filtroValueMonthIns = getFiltroValueMonthIns(filtroValue.meses);
    const updatedMesesIns = buildUpdatedMesesIns(
      calculatedMonthInsSelectedOptions,
      filtroValueMonthIns
    );
    const filteredMesesIns = updatedMesesIns.filter((mes) => mes.active === 'true');
    const combinedOptions = normalizeOptions([
      ...filteredAnyos,
      ...filteredMeses,
      ...filteredMesesIns,
      ...updatedInstructores,
    ]);
    const uniqueCombinedOptions = getUniqueOptions(combinedOptions);

    setOptionsFilter(JSON.stringify(uniqueCombinedOptions));
    setAnyoFilter(JSON.stringify(updatedAnyos.map(opt => ({
      ...opt,
      value: opt.value.toString()
    }))));
    setMesesFilter(JSON.stringify(updatedMeses.map(opt => ({
      ...opt,
      value: opt.value.toString()
    }))));
    filterRef.current = {
      anyo: filtroValue.anyo,
      meses: filtroValue.meses,
      instructores: filtroValue.instructores,
      elemInicio: filtroValue.elemInicio,
      elemFin: filtroValue.elemFin,
      lastProfile: filtroValue.lastProfile,
      savedValues: filtroValue.savedValues,
    };
  };

  const openModalGuardarFiltroCerrado = () => {
    setModalCerradoFiltroGuardarType('empty');
  }

  const closeModalGuardarFiltroCerrado = () => {
    setModalCerradoFiltroGuardarType('');
  };

  const handleClickConfirmFilterButton = async () => {
    const filtroDatos = filterRef.current;
    const nombrePag = links[0].label;

    dispatch(setFiltroNombrePagina(nombrePag));
    dispatch(setFiltroJson(filtroDatos));

    dispatch(guardarFiltroRequest(nombrePag, filtroDatos));

    if (profileActive?.ID_PERFIL === '211') {
      history.push(RouteTypes.REPORT_HOURS_INSTRUCTOR);
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      history.push(RouteTypes.AUDIT);
      history.push(RouteTypes.REPORT_HOURS_INSTRUCTOR);
    }
  };

  const handleChangeFiltroNombre = (e: string) => {
    dispatch(setFiltroNombre(e));
  };

  //Editar filtro
  const openModalEditarFiltroCerrado = () => {
    setModalCerradoFiltroEditarType('confirm');
  };

  const closeModalEditarFiltroCerrado = () => {
    setModalCerradoFiltroEditarType('');
  };

  const confirmModalEditarFiltroCerrado = (filterName: string) => {
    handleClickEditarFiltroButton(filterName);
  };

  const handleClickEditarFiltroButton = async (filterName: string) => {
    setModalCerradoFiltroEditarType('');

    const filtroDatos = filterRef.current;
    const filtroIdString = String(selectedFilterId);
    const nombrePag = links[0].label;
    const pagina = dispatch(setFiltroNombrePagina(nombrePag));
    const filtroJson = dispatch(setFiltroJson(filtroDatos));
    const nombreFiltro = filterName.split(' - ')[1];
    const filtroSeleccionado = filtro.find(filtro => filtro.NOMBRE_FILTRO === nombreFiltro);
    const fechaCreacion = filtroSeleccionado.FECHA_CREACION;
    const fechaModificacion = getActualTime();

    dispatch(setFiltroNombre(nombreFiltro));
    dispatch(setFiltroId(filtroIdString));
    dispatch(setFiltroFechaCreacion(fechaCreacion));
    dispatch(setFiltroFechaModificacion(fechaModificacion));
    dispatch(editarFiltroRequest(filtroJson, pagina));
    if (profileActive?.ID_PERFIL === '211') {
      history.push(RouteTypes.REPORT_HOURS_INSTRUCTOR);
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      history.push(RouteTypes.AUDIT);
      history.push(RouteTypes.REPORT_HOURS_INSTRUCTOR);
    }
  };

  //Eliminar filtro
  const openModalEliminarFiltroCerrado = () => {
    setModalCerradoFiltroEliminarType('confirm');
  }

  const closeModalEliminarFiltroCerrado = () => {
    setModalCerradoFiltroEliminarType('');
  }

  const confirmModalEliminarFiltroCerrado = () => {
    handleClickEliminarFiltroButton();
  };

  const handleClickEliminarFiltroButton = () => {
    setModalCerradoFiltroEliminarType('confirm');
    const filtroIdString = String(selectedFilterId);
    dispatch(setFiltroId(filtroIdString));
    dispatch(eliminarFiltroRequest(filtroIdString));

    if (profileActive?.ID_PERFIL === '211') {
      history.push(RouteTypes.REPORT_HOURS_INSTRUCTOR);
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      history.push(RouteTypes.AUDIT);
      history.push(RouteTypes.REPORT_HOURS_INSTRUCTOR);
    }
  };

  return (
    <>
      <RfUiAccordionCustom className="rf-accordion">
        <h2 className="rf-title rf-accordion__title">Filtrado</h2>
        <button
          className="rf-accordion__button rf-ui-accordion-custom__button"
          data-toggle="collapse"
          aria-expanded="false"
        >
          Mostrar filtros
        </button>
        <form
          id="courseForm"
          className="rf-accordion__content rf-ui-accordion-custom__panel collapse"
        >
          <div className="rf-box">
            <div className="rf-box__child">
              <div className="rf-practice-detail__input-container rf-practice-list__input-container--pr rf-practice-list__input-container--hasta-reducer-mb">
                <RfUiSelect
                  withoutOptionsText={language.validations.noOptions}
                  options={isFilterActive ? anyoFilter : calculatedAnyoSelectedOptions}
                  textError={language.validations.selectEmpty}
                  onOptionActive={handleChangeAnyoSelected}
                  label="Año"
                  placeholder="Seleccione año"
                  defaultValue={calculatedAnyoSelectedLabel}
                />
              </div>
            </div>
            <div className="rf-box__child">
              <div className="rf-practice-detail__input-container rf-practice-list__input-container--section-reducer-mb">
                {profileActive?.ID_PERFIL === '211' ? (
                  <>
                    <RfUiMultiselect
                      withoutOptionsText={language.validations.noOptions}
                      textError={language.validations.multiselectEmpty}
                      options={calculatedMonthInsSelectedOptions}
                      text-label="Mes"
                      placeholder="Seleccione mes"
                      defaultChecksRemake={selectedMonthIns}
                      onOptionSelected={handleMonthInsSelected}
                      optionsFilter={optionsFilter}
                      isFilterActive={isFilterActive}
                    />
                    <ErrorMessage message={errorMeses}></ErrorMessage>
                  </>
                ) : (
                  <RfUiSelect
                    withoutOptionsText={language.validations.noOptions}
                    options={isFilterActive ? mesesFilter : calculatedMonthSelectedOptions}
                    textError={language.validations.multiselectEmpty}
                    onOptionActive={handleChangeMonthSelected}
                    label="Mes"
                    placeholder="Seleccione mes"
                    defaultValue={calculatedMonthSelectedLabel}
                  /* isFilterActive={isFilterActive} */
                  />
                )}
              </div>
            </div>
          </div>
          <div className="rf-box">
            <div className="rf-practice-list__input-container">
              {profileActive?.ID_PERFIL === '211' ? (
                <RfUiInput
                  label="Instructor"
                  value={idUsuario + ' - ' + nombreUsuario}
                  disabled={true}
                  error=""
                  placeholder="Seleccione instructor"
                />
              ) : (
                <RfUiMultiselect
                  key={`multiselect-instructores-${instructorResetKey}`}
                  options={instructores}
                  defaultChecksRemake={selectedInstructores}
                  withoutOptionsText={language.validations.noOptions}
                  textError={language.validations.multiselectEmpty}
                  onOptionSelected={handleSelectedInstructor}
                  text-label="Instructor"
                  placeholder="Seleccione instructor"
                  optionsFilter={optionsFilter}
                  isFilterActive={isFilterActive}
                />
              )}
            </div>
            <div className="rf-practice-list__input-container">
              <RfUiSelect
                withoutOptionsText={language.validations.noOptions}
                textError={language.validations.multiselectEmpty}
                options={filtros}
                label="Filtros"
                onOptionActive={handleSelectedFiltros}
                placeHolder="Seleccione Filtro"
                /* defaultEmptyValue={selectedFiltros} */
                /* optionFilter={selectedOptionFilter} */
                onEdit={openModalEditarFiltroCerrado}
                onDelete={openModalEliminarFiltroCerrado}
              />
              {selectedOptionFilter && (
                <div className='butonsFilter'>
                  <><RfUiButton
                    text="Editar"
                    text-help="Editar filtro"
                    aspect="primary"
                    onButtonClick={() => {
                      openModalEditarFiltroCerrado();
                    }}
                  >
                    {' '}
                  </RfUiButton><RfUiButton
                    text="Eliminar"
                    text-help="Eliminar"
                    aspect="primary"
                    onButtonClick={() => {
                      openModalEliminarFiltroCerrado();
                    }}
                  >
                      {' '}
                    </RfUiButton></>
                </div>)}
            </div>
          </div>
          <div
            className={`rf-flex rf-justify-end rf-align-center rf-text-align-right
             ${isMobileOnly
                ? 'rf-block--mobile rf-text-align-center--mobile'
                : ''
              }`}
          >
            <RfUiButton
              text="Guardar filtros"
              text-help="Guardar filtros"
              aspect="secondary"
              onButtonClick={() => {
                openModalGuardarFiltroCerrado();
              }}
            >
              {' '}
            </RfUiButton>
            <RfUiButton
              text="Limpiar filtros"
              text-help="Limpiar filtros"
              aspect="secondary"
              onButtonClick={() => {
                cleanFilters();
              }}
            >
              {' '}
            </RfUiButton>
            <RfUiButton
              text="Filtrar"
              type="button"
              aspect="primary"
              className="rf-button rf-margin-left-30"
              onButtonClick={() => {
                handlerSubmitFilters();
              }}
              disabled={profileActive?.ID_PERFIL === '211' && !!errorMeses}
            >
              {' '}
            </RfUiButton>
          </div>
        </form>
        <div>
          <RfUiModal
            title-Modal={'Nombre filtro'}
            id-modal="empty"
            modal-type={modalCerradoFiltroGuardarType}
            slot-content={true}
            onModalClose={(e: any) => {
              closeModalGuardarFiltroCerrado();
            }}
            onConfirm={() => {
              handleClickConfirmFilterButton();
            }}
          >
            <div slot="content">
              <FilterContainer
                title={language.filter.filterName}
                valueFilter={nombreFiltro}
                handleFilterChange={handleChangeFiltroNombre}
              />
            </div>
            <div slot="content">
              <RfUiButton
                disabled={!filterNameAdd}
                text={language.filter.sendConfirm}
                type="button"
                aspect="white"
                className="rf-button"
                onButtonClick={handleClickConfirmFilterButton}
              >
              </RfUiButton>
            </div>
          </RfUiModal>
        </div>
        <div>
          <RfUiModal
            title-Modal={`Usted va a editar el filtro: ${selectedFilterName}`}
            id-modal="modalEdit"
            modal-type={modalCerradoFiltroEditarType}
            required-slot={false}
            onModalClose={(e: any) => {
              closeModalEditarFiltroCerrado();
            }}
            onConfirm={() => {
              confirmModalEditarFiltroCerrado(selectedFilterName);
            }}
          >
          </RfUiModal>
        </div>
        <div>
          <RfUiModal
            title-Modal={`¿Está seguro de que quiere eliminar el filtro? ${selectedFilterName}`}
            id-modal="modalDelete"
            modal-type={modalCerradoFiltroEliminarType}
            required-slot={false}
            onModalClose={(e: any) => {
              closeModalEliminarFiltroCerrado();
            }}
            onConfirm={confirmModalEliminarFiltroCerrado}
          >
          </RfUiModal>
        </div>
      </RfUiAccordionCustom>
    </>
  );
};
